import _ from 'lodash'
import generic from './../genericModule'
import { Hub } from 'services'
import { ADD, DELETE, SET, UPDATE, MARK_INITIALIZED } from '../../mutation-types'
import {
  CREATE,
  GET_BY_ID,
  UPDATE as UPDATE_ACTION,
  GET_ALL,
  DELETE as DELETE_ACTION,
  GET_FOR_APP,
  GET_FOR_FLOW,
  GET_FOR_MAINTAINER,
  GET_BY_NAME,
  ADD_AFFILIATE,
  REMOVE_AFFILIATE
} from '../../action-types'

// Get all fulfilled promises
function fulfilled(promises) {
  return _.map(_.filter(promises, p => p.status === 'fulfilled'), 'value')
}

// Get all rejected promises
function rejected(promises) {
  return _.filter(promises, p => p.status === 'rejected')
}

// Log all rejected promises
function handleFailures(promises) {
  const failed = rejected(promises)
  if (failed?.length) {
    console.error('Failed to read from hubs', failed)
  }
}

// Since mutations are synchronous and cannot alter state in e.g. callbacks from an async
// call then we need to patch the actions to:
//
// 1. dispatch a secondary action (`readFromHub`) , which
// 2. does an async request and in the callback then,
// 3. commits a new changes/mutations (`connections` etc), which
// 4. updates the Hub state with the result of the request.

export default {
  ...generic,
  state: () => ({
    ...generic.state,
    ...{
      hubLoadingErr: null,
      connection: [],
      hubs: {},
      categories: [],
      applications: [],
      licenses: [],
      items: []
    }
  }),
  actions: {
    ...generic.actions,
    [GET_ALL]({ dispatch, commit, state }, { typename }) {
      return generic.actions[GET_ALL]({ commit, state }, { typename }).then(items => {
        dispatch('readFromHub', { useCache: true })
        return items
      })
    },
    // Whenever we create, delete or update we need to refetch the connections again
    // This could be done a bit more cleverly by targeting the hubs that were changed,
    // but no such optimization is currently needed.
    [CREATE](s, args) {
      return generic.actions[CREATE](s, args).then(res => {
        s.dispatch('readFromHub', { useCache: false })
        return res
      })
    },
    [DELETE](s, args) {
      return generic.actions[DELETE](s, args).then(res => {
        s.dispatch('readFromHub', { useCache: false })
        return res
      })
    },
    [UPDATE](s, args) {
      return generic.actions[UPDATE](s, args).then(res => {
        s.dispatch('readFromHub', { useCache: false })
        return res
      })
    },

    // Read misc info from hubs
    readFromHub: ({ commit, state }, { useCache }) => {
      // This is mostly static data, we only get it once (refresh page to reload)
      if (state.connections && useCache) return

      // Create connections
      Promise.allSettled(_.map(state.items, Hub.connectionFor))
        .then(connections => {
          const okConnections = fulfilled(connections)

          commit('connections', { connections: okConnections })
          handleFailures(connections)

          // Read about info from hubs
          return Promise.allSettled([
            Promise.allSettled(_.map(okConnections, Hub.about)).then(abouts => {
              commit('hubs', _.keyBy(_.map(fulfilled(abouts), 'body'), a => a.identifier))
              handleFailures(abouts)
            }),

            // Load categories from hubs
            Promise.allSettled(_.flatMap(okConnections, Hub.categories)).then(categories => {
              commit('categories', _.flatMap(fulfilled(categories), c => c.body.categories))
              handleFailures(categories)
            }),

            // Load apps from hubs
            Promise.allSettled(_.flatMap(okConnections, Hub.applications)).then(applications => {
              commit('applications', _.flatMap(fulfilled(applications), c => c.body.applications))
              handleFailures(applications)
            }),

            // Load license (templates) from hubs
            Promise.allSettled(_.flatMap(okConnections, Hub.licenses)).then(licenses => {
              commit('licenses', _.flatMap(fulfilled(licenses), c => c.body.licenses))
              handleFailures(licenses)
            })
          ])
        })
        .then(r => {
          commit('hubLoadingErr', null)
          return r
        })
        .catch(err => {
          console.error('Error loading hubs', err)
          commit('hubLoadingErr', err)
        })
    }
  },
  mutations: {
    ...generic.mutations,
    connections(state, { connections }) {
      state.connections = connections
    },

    hubs(state, hubs) {
      state.hubs = hubs
    },

    categories(state, categories) {
      state.categories = categories
    },

    applications(state, applications) {
      state.applications = applications
    },

    licenses(state, licenses) {
      state.licenses = licenses
    },

    hubLoadingErr(state, err) {
      state.loadingErr = err
    }
  }
}
